import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import {
  deleteEmployerByIdCall,
  getEmployeesCall,
  postEmployeesDeleteCheckCall,
} from '@/people/state';
import { SanityWarningType } from '@/swagger/constants/anagraphics-enums';
import { Routes } from '../../constants/routes';
import { useNotification } from '../../hooks';
import { AlertBadge } from '../AlertBadge';
import { Dialog } from '../Dialog';

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  employeeId: string;
};

export const DeleteProfile = ({ isOpen, closeDialog, employeeId }: Props) => {
  const t = useTranslations('profileCard.dialogs.deleteProfile');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const gapXXS = convertSizeToNumber(`${useThemeValue('sizes', 'xxs')}`);
  const { execute: executeDeleteEmployerById, loading: loadingDeleteEmployerById } =
    deleteEmployerByIdCall.hook();

  const navigate = useNavigate();

  const { execute: executeGetEmployees } = getEmployeesCall.hook();

  const {
    execute: executePostEmployeesDeleteCheck,
    deleteCheckResponse,
    loading: loadingPostEmployeesDeleteCheck,
  } = postEmployeesDeleteCheckCall.hook();

  const onConfirm = useCallback(() => {
    executeDeleteEmployerById({
      employeeId,
      successSaga: () => {
        notify(tShared('success.genericApiSuccess'), 'success');
        closeDialog();
        executeGetEmployees({});
        navigate(Routes.People);
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [
    executeDeleteEmployerById,
    employeeId,
    notify,
    tShared,
    closeDialog,
    executeGetEmployees,
    navigate,
  ]);

  const warnings = deleteCheckResponse?.warnings ?? [];

  useEffect(() => {
    if (isOpen) {
      executePostEmployeesDeleteCheck({ employeeIds: [employeeId] });
    }
  }, [isOpen, employeeId, executePostEmployeesDeleteCheck]);

  return (
    <Dialog
      maxWidth='550px'
      name='deleteProfile'
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mb='xs' mt='m' width='100%'>
        <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
        <Typography variant='p2/light' fontWeight='bold' color='primary/text' textAlign='center'>
          {t('confirmation')}
        </Typography>

        {!loadingPostEmployeesDeleteCheck && warnings.length > 0 && (
          <Box mt='s' display='flex' flexDirection='column' style={{ gap: gapXXS }}>
            {warnings.map(warning => (
              <AlertBadge
                key={`${warning.type}-${warning.departmentKey}`}
                type='error'
                iconName='info'
                label={
                  warning.type === SanityWarningType.DEPARTMENT_LOSES_MANAGER
                    ? t('warnings.departmentLosesManager', {
                        departmentName: warning.departmentName,
                      })
                    : t('warnings.departmentBecomesEmpty', {
                        departmentName: warning.departmentName,
                      })
                }
              />
            ))}
          </Box>
        )}

        <Box mt='s' alignItems='center' display='flex' flexDirection='row' justifyContent='center'>
          <Button variant='link/primary' onClick={() => closeDialog()} mr='s' mt='s'>
            {t('buttons.cancel')}
          </Button>
          <Button
            loading={loadingDeleteEmployerById}
            disabled={loadingDeleteEmployerById}
            mt='s'
            data-testid='button-remove-profile'
            onClick={onConfirm}
          >
            {t('buttons.confirm')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
